import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CheckCircle2, Store, TrendingUp, Sparkles, X, Users, Search, Award, Gift, Building2, Camera } from 'lucide-react';
import { useLocale, PRICING } from '@/contexts/LocaleContext';

const PRO_TRIAL_DAYS = 7;

const PricingPlans = ({ showHeader = true, onActivate }) => {
  const navigate = useNavigate();
  const { region, currency, isUSA } = useLocale();

  const pricing = PRICING[region].pro;
  const enterprise = PRICING[region].enterprise;
  const periodLabel = isUSA ? 'month' : 'mes';

  const handleActivate = () => {
    if (onActivate) {
      onActivate();
    } else {
      navigate('/register?plan=pro');
    }
  };

  const freeFeatures = isUSA ? [
    { text: 'Up to 5 products', included: true },
    { text: 'Basic profile in the directory', included: true },
    { text: 'Basic online store (with "Made with TexHub" badge)', included: true },
    { text: 'AI product sheet from a photo', included: true },
    { text: '20 AI credits to try TexHub AI', included: true },
    { text: 'Priority in search & featured badge', included: false },
    { text: 'Unlimited products', included: false }
  ] : [
    { text: 'Hasta 5 productos', included: true },
    { text: 'Perfil básico en el directorio', included: true },
    { text: 'Tienda en línea básica (con sello "Hecho con TexHub")', included: true },
    { text: 'Ficha de producto con IA desde una foto', included: true },
    { text: '20 créditos IA para probar TexHub AI', included: true },
    { text: 'Prioridad en búsqueda y badge destacado', included: false },
    { text: 'Productos ilimitados', included: false }
  ];

  const proFeatures = isUSA ? [
    { icon: TrendingUp, text: 'Unlimited products' },
    { icon: Store, text: 'Online store without the TexHub badge' },
    { icon: Search, text: 'Priority in search results' },
    { icon: Award, text: 'Featured supplier badge' },
    { icon: Sparkles, text: `Full AI tools (${pricing.aiCredits} credits/month)*` },
    { icon: Users, text: 'Priority support' }
  ] : [
    { icon: TrendingUp, text: 'Productos ilimitados' },
    { icon: Store, text: 'Tienda en línea sin sello TexHub' },
    { icon: Search, text: 'Prioridad en resultados de búsqueda' },
    { icon: Award, text: 'Badge de proveedor destacado' },
    { icon: Sparkles, text: `Herramientas IA completas (${pricing.aiCredits} créditos/mes)*` },
    { icon: Users, text: 'Soporte prioritario' }
  ];

  const enterpriseFeatures = isUSA ? [
    { icon: CheckCircle2, text: 'Everything in PRO' },
    { icon: Sparkles, text: `${enterprise.aiCredits} AI credits/month` },
    { icon: Users, text: 'Multi-user team accounts' },
    { icon: Building2, text: 'Dedicated account manager' }
  ] : [
    { icon: CheckCircle2, text: 'Todo lo de PRO' },
    { icon: Sparkles, text: `${enterprise.aiCredits} créditos IA/mes` },
    { icon: Users, text: 'Cuentas de equipo multiusuario' },
    { icon: Building2, text: 'Ejecutivo de cuenta dedicado' }
  ];

  return (
    <div data-testid="pricing-plans" className="w-full max-w-6xl mx-auto">
      {showHeader && (
        <div className="text-center mb-8">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900" style={{fontFamily: 'Manrope, sans-serif'}}>
            {isUSA ? 'Choose your plan' : 'Elige tu plan'}
          </h2>
          <p className="text-slate-600 mt-2">
            {isUSA ? 'Start free, upgrade when you grow' : 'Empieza gratis, actualiza cuando crezcas'}
          </p>
        </div>
      )}

      <div className="grid md:grid-cols-3 gap-6 items-start">
        {/* Free Plan */}
        <Card className="bg-white border border-slate-200 shadow-sm" data-testid="plan-free-card">
          <CardContent className="p-6">
            <div className="text-center mb-6">
              <Badge className="bg-slate-100 text-slate-700 px-4 py-1 text-sm font-semibold mb-3">
                {isUSA ? 'Free' : 'Gratis'}
              </Badge>
              <div className="flex items-baseline justify-center gap-1">
                <span className="text-4xl font-bold text-slate-900">$0</span>
                <span className="text-slate-500">/{periodLabel}</span>
              </div>
              <p className="text-slate-500 text-sm mt-2">
                {isUSA ? 'No credit card required' : 'Sin tarjeta de crédito'}
              </p>
            </div>

            <ul className="space-y-3 mb-6">
              {freeFeatures.map((feature, index) => (
                <li key={index} className="flex items-start gap-3 text-slate-600">
                  {feature.included ? (
                    <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                  ) : (
                    <X className="w-5 h-5 text-slate-300 flex-shrink-0 mt-0.5" />
                  )}
                  <span className={feature.included ? 'text-sm' : 'text-sm text-slate-400'}>{feature.text}</span>
                </li>
              ))}
            </ul>

            <Button
              variant="outline"
              className="w-full font-semibold py-5"
              onClick={() => navigate('/register')}
              data-testid="plan-free-btn"
            >
              {isUSA ? 'Start free' : 'Empieza gratis'}
            </Button>
          </CardContent>
        </Card>

        {/* Pro Plan */}
        <Card className="bg-white border-2 border-emerald-500 shadow-xl relative overflow-hidden md:-mt-4" data-testid="plan-pro-card">
          <div className="absolute top-4 right-4">
            <Badge className="bg-emerald-500 text-white px-3 py-1">
              {isUSA ? 'Most popular' : 'Más popular'}
            </Badge>
          </div>

          <CardContent className="p-6">
            <div className="text-center mb-6">
              <Badge className="bg-emerald-100 text-emerald-700 px-4 py-1 text-sm font-semibold mb-3">
                TexHub Pro
              </Badge>
              <div className="flex items-baseline justify-center gap-1">
                <span className="text-4xl font-bold text-slate-900">${pricing.price}</span>
                <span className="text-slate-500">{currency}/{periodLabel}</span>
              </div>
              <div
                className="inline-flex items-center gap-1.5 mt-3 px-3 py-1.5 rounded-full bg-amber-50 border border-amber-200 text-amber-800 text-sm font-semibold"
                data-testid="plan-pro-trial-badge"
              >
                <Gift className="w-4 h-4" />
                {isUSA ? `Try PRO ${PRO_TRIAL_DAYS} days free` : `Prueba PRO ${PRO_TRIAL_DAYS} días gratis`}
              </div>
            </div>

            <ul className="space-y-3 mb-6">
              {proFeatures.map((feature, index) => (
                <li key={index} className="flex items-center gap-3 text-slate-700">
                  <div className="w-6 h-6 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <feature.icon className="w-3 h-3 text-emerald-600" />
                  </div>
                  <span className="font-medium text-sm">{feature.text}</span>
                </li>
              ))}
            </ul>

            <Button
              className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-bold py-5"
              onClick={handleActivate}
              data-testid="plan-pro-btn"
            >
              {isUSA ? `Try PRO ${PRO_TRIAL_DAYS} days free` : `Prueba PRO ${PRO_TRIAL_DAYS} días gratis`}
            </Button>

            <p className="text-xs text-slate-500 text-center mt-4">
              {isUSA
                ? `Then $${pricing.price} ${currency}/${periodLabel}. Cancel anytime.`
                : `Después $${pricing.price} ${currency}/${periodLabel}. Cancela cuando quieras.`}
            </p>

            <div className="mt-4 pt-4 border-t border-slate-200">
              <p className="text-xs text-slate-500">
                <span className="font-semibold">*{isUSA ? 'AI Tools include:' : 'Herramientas de IA incluyen:'}</span>
                {' '}
                {isUSA
                  ? 'Fashion Designer, Visual Search, Photo Optimizer, TexTrend AI and TexChat AI.'
                  : 'Diseñador de Modas, Buscador Visual, Optimizador de Fotos, TexTrend AI y TexChat AI.'}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Enterprise Plan */}
        <Card className="bg-slate-900 border border-slate-800 shadow-sm text-white" data-testid="plan-enterprise-card">
          <CardContent className="p-6">
            <div className="text-center mb-6">
              <Badge className="bg-slate-700 text-slate-100 px-4 py-1 text-sm font-semibold mb-3">
                {isUSA ? 'Enterprise' : 'Empresarial'}
              </Badge>
              <div className="flex items-baseline justify-center gap-1">
                <span className="text-4xl font-bold">${enterprise.price.toLocaleString(isUSA ? 'en-US' : 'es-MX')}</span>
                <span className="text-slate-400">{currency}/{periodLabel}</span>
              </div>
              <p className="text-slate-400 text-sm mt-2">
                {isUSA ? 'For teams and high-volume suppliers' : 'Para equipos y proveedores de alto volumen'}
              </p>
            </div>

            <ul className="space-y-3 mb-6">
              {enterpriseFeatures.map((feature, index) => (
                <li key={index} className="flex items-center gap-3 text-slate-200">
                  <div className="w-6 h-6 bg-slate-800 rounded-full flex items-center justify-center flex-shrink-0">
                    <feature.icon className="w-3 h-3 text-emerald-400" />
                  </div>
                  <span className="font-medium text-sm">{feature.text}</span>
                </li>
              ))}
            </ul>

            <Button
              variant="outline"
              className="w-full font-semibold py-5 bg-transparent border-slate-600 text-white hover:bg-slate-800 hover:text-white"
              onClick={() => navigate('/register?plan=enterprise')}
              data-testid="plan-enterprise-btn"
            >
              {isUSA ? 'Get Enterprise' : 'Quiero Empresarial'}
            </Button>
          </CardContent>
        </Card>
      </div>

      <p className="text-center text-xs text-slate-500 mt-6 flex items-center justify-center gap-1.5" data-testid="pricing-photo-ai-note">
        <Camera className="w-3.5 h-3.5" />
        {isUSA
          ? 'All plans: upload a photo and AI fills in your product sheet.'
          : 'Todos los planes: sube una foto y la IA llena la ficha de tu producto.'}
      </p>
    </div>
  );
};

export default PricingPlans;
